/**
 * Contact Page Module
 * Uses Firebase Realtime Database for form submissions
 */

import router from './router.js';

class ContactPage {
  constructor() {
    this.container = null;
    this.db = null;
    this.firebaseReady = false;
  }

  initFirebase() {
    if (this.firebaseReady) return;

    const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
    const databaseURL = import.meta.env.VITE_FIREBASE_DATABASE_URL;
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;

    if (!apiKey || !databaseURL || !projectId) {
      console.warn('ContactPage: Firebase env vars not set. Form submissions will not work.');
      return;
    }

    try {
      const app = firebase.initializeApp({
        apiKey,
        databaseURL,
        projectId
      }, 'contact-form');
      this.db = app.database();
      this.firebaseReady = true;
    } catch (e) {
      // App may already be initialized
      try {
        const app = firebase.app('contact-form');
        this.db = app.database();
        this.firebaseReady = true;
      } catch (e2) {
        console.error('ContactPage: Failed to initialize Firebase:', e2);
      }
    }
  }

  render() {
    const app = document.getElementById('app');
    const hero = document.getElementById('hero');
    if (app) app.style.display = 'none';
    if (hero) hero.style.display = 'none';

    if (!this.container) {
      this.container = document.createElement('div');
      this.container.id = 'contact-page';
      this.container.className = 'static-page';
      this.setupEventListeners();
    }

    this.container.innerHTML = `
      <div class="static-page-header">
        <button class="back-btn" id="back-btn" aria-label="Go back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back
        </button>
        <h1 class="static-page-title">Contact Us</h1>
      </div>

      <div class="static-page-content">
        <p class="contact-intro">Have a question, suggestion, or want to report an issue? Fill out the form below and we'll get back to you as soon as possible.</p>

        <form id="contact-form" class="contact-form" novalidate>
          <div class="form-group">
            <label for="contact-name" class="form-label">Name <span class="required">*</span></label>
            <input type="text" id="contact-name" class="form-input" placeholder="Your name" required>
            <span class="form-error" id="name-error"></span>
          </div>

          <div class="form-group">
            <label for="contact-email" class="form-label">Email <span class="required">*</span></label>
            <input type="email" id="contact-email" class="form-input" placeholder="you@example.com" required>
            <span class="form-error" id="email-error"></span>
          </div>

          <div class="form-group">
            <label for="contact-subject" class="form-label">Subject <span class="required">*</span></label>
            <input type="text" id="contact-subject" class="form-input" placeholder="What is this about?" required>
            <span class="form-error" id="subject-error"></span>
          </div>

          <div class="form-group">
            <label for="contact-message" class="form-label">Message <span class="required">*</span></label>
            <textarea id="contact-message" class="form-input form-textarea" placeholder="Your message..." rows="6" required></textarea>
            <span class="form-error" id="message-error"></span>
          </div>

          <button type="submit" class="btn btn-primary contact-submit" id="contact-submit">
            Send Message
          </button>

          <div id="contact-status" class="contact-status"></div>
        </form>
      </div>
    `;

    if (!document.body.contains(this.container)) {
      const footer = document.querySelector('.footer');
      if (footer) {
        document.body.insertBefore(this.container, footer);
      } else {
        document.body.appendChild(this.container);
      }
    }

    this.container.style.display = 'block';
    window.scrollTo({ top: 0, behavior: 'instant' });

    // Initialize Firebase lazily on first render
    this.initFirebase();
  }

  setupEventListeners() {
    this.container.addEventListener('click', (e) => {
      if (e.target.closest('#back-btn')) {
        router.back();
        return;
      }
    });

    this.container.addEventListener('submit', (e) => {
      if (e.target.id === 'contact-form') {
        e.preventDefault();
        this.handleSubmit();
      }
    });
  }

  validateForm() {
    let valid = true;

    const name = this.container.querySelector('#contact-name');
    const email = this.container.querySelector('#contact-email');
    const subject = this.container.querySelector('#contact-subject');
    const message = this.container.querySelector('#contact-message');

    // Clear previous errors
    this.container.querySelectorAll('.form-error').forEach(el => el.textContent = '');
    this.container.querySelectorAll('.form-input').forEach(el => el.classList.remove('invalid'));

    if (!name.value.trim()) {
      this.showFieldError('name', 'Name is required');
      valid = false;
    }

    if (!email.value.trim()) {
      this.showFieldError('email', 'Email is required');
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) {
      this.showFieldError('email', 'Please enter a valid email address');
      valid = false;
    }

    if (!subject.value.trim()) {
      this.showFieldError('subject', 'Subject is required');
      valid = false;
    }

    if (!message.value.trim()) {
      this.showFieldError('message', 'Message is required');
      valid = false;
    }

    return valid;
  }

  showFieldError(field, message) {
    const errorEl = this.container.querySelector(`#${field}-error`);
    const inputEl = this.container.querySelector(`#contact-${field}`);
    if (errorEl) errorEl.textContent = message;
    if (inputEl) inputEl.classList.add('invalid');
  }

  async handleSubmit() {
    if (!this.validateForm()) return;

    const submitBtn = this.container.querySelector('#contact-submit');
    const statusEl = this.container.querySelector('#contact-status');

    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending...';
    statusEl.textContent = '';
    statusEl.className = 'contact-status';

    const data = {
      name: this.container.querySelector('#contact-name').value.trim(),
      email: this.container.querySelector('#contact-email').value.trim(),
      subject: this.container.querySelector('#contact-subject').value.trim(),
      message: this.container.querySelector('#contact-message').value.trim(),
      timestamp: new Date().toISOString()
    };

    if (!this.firebaseReady || !this.db) {
      statusEl.textContent = 'Contact form is not configured yet. Please try again later.';
      statusEl.classList.add('error');
      submitBtn.disabled = false;
      submitBtn.textContent = 'Send Message';
      return;
    }

    try {
      await this.db.ref('contact-submissions').push(data);

      statusEl.textContent = 'Message sent successfully! We\'ll get back to you soon.';
      statusEl.classList.add('success');

      // Reset form
      this.container.querySelector('#contact-form').reset();
    } catch (error) {
      console.error('ContactPage: Submit failed:', error);
      statusEl.textContent = 'Failed to send message. Please try again later.';
      statusEl.classList.add('error');
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Send Message';
    }
  }

  hide() {
    if (this.container) {
      this.container.style.display = 'none';
    }
    const app = document.getElementById('app');
    const hero = document.getElementById('hero');
    if (app) app.style.display = 'block';
    if (hero) hero.style.display = 'block';
  }
}

export default new ContactPage();
